import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleIcon, AppleIcon } from './SocialIcons';
import { useAuth } from "../contexts/AuthContext.jsx";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import './Login.css';

// Map raw error codes/messages → friendly bilingual messages
const getFriendlyError = (rawError, lang) => {
  const msg = (rawError || '').toLowerCase();

  // Firebase / backend error codes → friendly messages
  const errorMap = [
    {
      match: ['auth/wrong-password', 'auth/invalid-credential', 'invalid credentials', 'invalid password', 'incorrect password', 'wrong password', 'invalid-credential'],
      vi: '❌ Mật khẩu không chính xác. Vui lòng kiểm tra lại.',
      en: '❌ Incorrect password. Please check and try again.',
    },
    {
      match: ['auth/user-not-found', 'user not found', 'no user', 'not found'],
      vi: '❌ Email này chưa được đăng ký. Hãy tạo tài khoản mới.',
      en: '❌ This email is not registered. Please create an account.',
    },
    {
      match: ['auth/invalid-email', 'invalid email'],
      vi: '❌ Địa chỉ email không hợp lệ.',
      en: '❌ Invalid email address.',
    },
    {
      match: ['auth/email-already-in-use', 'email already'],
      vi: '❌ Email này đã được sử dụng bởi tài khoản khác.',
      en: '❌ This email is already in use by another account.',
    },
    {
      match: ['auth/user-disabled', 'account disabled', 'user disabled', 'account has been disabled'],
      vi: '🚫 Tài khoản này đã bị vô hiệu hóa. Vui lòng liên hệ hỗ trợ.',
      en: '🚫 This account has been disabled. Please contact support.',
    },
    {
      match: ['auth/too-many-requests', 'too many', 'rate limit', 'too many attempts'],
      vi: '⏳ Đăng nhập thất bại quá nhiều lần. Hãy thử lại sau vài phút.',
      en: '⏳ Too many failed attempts. Please wait a few minutes and try again.',
    },
    {
      match: ['auth/popup-closed-by-user', 'popup closed', 'popup-closed'],
      vi: '⚠️ Cửa sổ đăng nhập đã bị đóng. Vui lòng thử lại.',
      en: '⚠️ Login window was closed. Please try again.',
    },
    {
      match: ['auth/popup-blocked', 'popup blocked'],
      vi: '⚠️ Trình duyệt đã chặn cửa sổ popup. Hãy cho phép popup rồi thử lại.',
      en: '⚠️ Popup was blocked by your browser. Please allow popups and try again.',
    },
    {
      match: ['auth/network-request-failed', 'network', 'fetch', 'internet', 'connection'],
      vi: '🌐 Không có kết nối mạng. Vui lòng kiểm tra Wi-Fi hoặc dữ liệu di động.',
      en: '🌐 No internet connection. Please check your Wi-Fi or mobile data.',
    },
    {
      match: ['auth/account-exists-with-different-credential', 'account-exists'],
      vi: '⚠️ Email này đã được liên kết với phương thức đăng nhập khác (Google/Apple).',
      en: '⚠️ This email is linked to a different sign-in method (Google/Apple).',
    },
    {
      match: ['feature unavailable', 'facebook login is currently unavailable'],
      vi: '⚠️ Đăng nhập Facebook hiện chưa khả dụng. Vui lòng dùng Google hoặc Email.',
      en: '⚠️ Facebook login is currently unavailable. Please use Google or Email.',
    },
    {
      match: ['auth/cancelled-popup-request'],
      vi: '⚠️ Đã hủy yêu cầu đăng nhập trước đó. Vui lòng thử lại.',
      en: '⚠️ Previous login request was cancelled. Please try again.',
    },
    {
      match: ['token'],
      vi: '❌ Phiên đăng nhập không hợp lệ. Vui lòng thử lại.',
      en: '❌ Invalid session token. Please try again.',
    },
  ];

  for (const entry of errorMap) {
    if (entry.match.some(keyword => msg.includes(keyword))) {
      return lang === 'vi' ? entry.vi : entry.en;
    }
  }

  // Fallback: show original message if not matched
  if (rawError && rawError.length < 120) return rawError;
  return lang === 'vi' ? '❌ Đăng nhập thất bại. Vui lòng thử lại.' : '❌ Login failed. Please try again.';
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading: authLoading, loginWithGoogle, loginWithApple } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Detect in-app browsers (Facebook, Messenger, Zalo, Instagram)
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line|Zalo|Viber/i.test(navigator.userAgent);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      setError('Vui lòng nhập Email và mật khẩu');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Gọi API login thực tế thông qua AuthContext
      const result = await login(identifier, password);

      if (result.success) {
        // Lấy trang đích từ state hoặc mặc định là dashboard
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setError(getFriendlyError(result.error, language));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(getFriendlyError(err.message, language));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsSubmitting(true);
    setError('');

    let result;
    if (provider === 'Google') {
      result = await loginWithGoogle();
    } else if (provider === 'Apple') {
      result = await loginWithApple();
    }

    if (result && result.success) {
      // New users or users without a business → redirect to onboarding
      if (result.isNewUser || result.hasNoBusiness) {
        navigate('/onboarding', { replace: true });
      } else {
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } else {
      const providerName = provider === 'Google' ? 'Google' : provider === 'Facebook' ? 'Facebook' : 'Apple';
      setError(getFriendlyError(result?.error, language) || (language === 'vi' ? `Đăng nhập ${providerName} thất bại.` : `${providerName} login failed.`));
      setIsSubmitting(false);
    }
  };

  // Hiển thị loading nếu AuthContext đang loading
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
          <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
          <p style={{ marginTop: '20px', color: '#4a5568', fontWeight: 'bold' }}>{t('login.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="lang-switch-container" style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(255, 255, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          borderRadius: '9999px',
          padding: '6px 20px',
          backdropFilter: 'blur(10px)',
          color: 'white',
          fontWeight: '700',
          fontSize: '1rem',
          letterSpacing: '1px'
        }}>
          <span 
            onClick={() => setLanguage('vi')}
            style={{ 
              cursor: 'pointer', 
              opacity: language === 'vi' ? 1 : 0.5,
              borderBottom: language === 'vi' ? '2px solid white' : '2px solid transparent',
              paddingBottom: '2px',
              transition: 'all 0.2s'
            }}>VN</span>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>/</span>
          <span 
            onClick={() => setLanguage('en')}
            style={{ 
              cursor: 'pointer', 
              opacity: language === 'en' ? 1 : 0.5,
              borderBottom: language === 'en' ? '2px solid white' : '2px solid transparent',
              paddingBottom: '2px',
              transition: 'all 0.2s'
            }}>EN</span>
        </div>
      </div>

      <div className="login-card">
        {/* Logo/Title */}
        <div className="logo-container">
          <h1 className="logo-text">{t('login.title')}</h1>
        </div>

        {/* Hiển thị lỗi */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Form đăng nhập */}
        <form className="login-form" onSubmit={handleSubmit}>
          {/* Email Field */}
          <div className="form-group">
            <label className="form-label">{t('login.email') || "Email"}</label>
            <input
              type="email"
              className="form-input"
              placeholder="name@example.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={isSubmitting || authLoading}
              autoComplete="email"
              autoCapitalize="none"
            />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label className="form-label">{t('login.password')}</label>
            <input
              type="password"
              className="form-input"
              placeholder={t('login.placeholderPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting || authLoading}
              autoComplete="current-password"
            />
          </div>

          {/* Buttons */}
          <div className="login-buttons">
            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || authLoading}
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner"></span>
                  {t('login.loading')}
                </>
              ) : t('login.loginButton')}
            </button>
            <button
              type="button"
              className="register-button primary"
              onClick={() => navigate('/register')}
              disabled={isSubmitting || authLoading}
            >
              {t('login.registerButton')}
            </button>
          </div>
        </form>

        {/* Divider với text "Or login with" */}
        <div className="divider-container">
          <div className="divider-line"></div>
          <span className="divider-text">{t('login.divider')}</span>
          <div className="divider-line"></div>
        </div>

        {/* Social Login Buttons tương tự Register.css style */}
        <div className="social-buttons-container" style={{ position: 'relative' }}>
          {isInAppBrowser && (
            <div style={{ position: 'absolute', top: '-60px', left: 0, right: 0, background: '#fffbeb', color: '#b45309', padding: '8px', borderRadius: '8px', fontSize: '12px', textAlign: 'center', border: '1px solid #fcd34d', zIndex: 10 }}>
              {language === 'vi' 
                ? '⚠️ Vui lòng mở bằng Safari/Chrome để đăng nhập bằng Google.' 
                : '⚠️ Please open in Safari/Chrome to use Google Login.'}
            </div>
          )}
          <button
            className={`social-button google ${isInAppBrowser ? 'disabled' : ''}`}
            onClick={() => !isInAppBrowser && handleSocialLogin('Google')}
            title="Google"
            style={{ opacity: isInAppBrowser ? 0.5 : 1, cursor: isInAppBrowser ? 'not-allowed' : 'pointer' }}
            disabled={isInAppBrowser}
          >
            <GoogleIcon />
          </button>

          <button
            className="social-button apple"
            onClick={() => handleSocialLogin('Apple')}
            title="Apple"
          >
            <AppleIcon />
          </button>
        </div>

        {/* Legal Links Footer */}
        <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '13px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
          {language === 'vi' ? 'Về chúng tôi: ' : 'About Us: '}
          <Link to="/terms-of-service" style={{ color: '#4f46e5', textDecoration: 'none', margin: '0 5px' }}>
            {language === 'vi' ? 'Điều khoản dịch vụ' : 'Terms of Service'}
          </Link>
          |
          <Link to="/privacy-policy" style={{ color: '#4f46e5', textDecoration: 'none', margin: '0 5px' }}>
            {language === 'vi' ? 'Chính sách bảo mật' : 'Privacy Policy'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;